import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Alert,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Conditions, StringCondition, NumericCondition, ValidationResult } from '../../types/dynamicRouting';

interface ConditionsEditorProps {
  conditions: Conditions;
  onChange: (conditions: Conditions) => void;
  validationResult?: ValidationResult;
  conditionsPath: string;
}

const ConditionsEditor: React.FC<ConditionsEditorProps> = ({
  conditions,
  onChange,
  validationResult,
  conditionsPath,
}) => {
  const handleConditionChange = (field: keyof Conditions, value: any) => {
    onChange({
      ...conditions,
      [field]: value,
    });
  };

  const handleStringConditionChange = (field: keyof Conditions, conditionField: keyof StringCondition, value: any) => {
    const currentCondition = conditions[field] as StringCondition || { operator: 'equals', value: '' };
    handleConditionChange(field, {
      ...currentCondition,
      [conditionField]: value,
    });
  };

  const handleNumericConditionChange = (field: keyof Conditions, conditionField: keyof NumericCondition, value: any) => {
    const currentCondition = conditions[field] as NumericCondition || { operator: 'equals', value: 0 };
    handleConditionChange(field, {
      ...currentCondition,
      [conditionField]: value,
    });
  };

  const handleRemoveCondition = (field: keyof Conditions) => {
    const newConditions = { ...conditions };
    delete newConditions[field];
    onChange(newConditions);
  };

  // Helper function to get validation messages for a specific condition
  const getConditionValidationMessages = (field: string) => {
    if (!validationResult) return [];
    return validationResult.messages.filter(msg =>
      msg.path.includes(`${conditionsPath}.${field}`) || msg.path.includes(`conditions.${field}`)
    );
  };

  const stringOperators = [
    { value: 'equals', label: 'Equals' },
    { value: 'in', label: 'In (any of)' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'not_in', label: 'Not In (none of)' },
    { value: 'contains', label: 'Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
  ];

  const numericOperators = [
    { value: 'equals', label: 'Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'greater_than_or_equal', label: 'Greater Than or Equal' },
    { value: 'less_than_or_equal', label: 'Less Than or Equal' },
    { value: 'between', label: 'Between' },
  ];

  const transactionTypes = ['Lumpsum', 'Emandate', 'Hybrid'];
  const paymentModes = ['UPI', 'NetBanking', 'Debit Card', 'OTM'];
  const authModes = ['Net Banking', 'Debit Card', 'PAN', 'AADHAR', 'UPI'];
  const commonBanks = ['HDFC', 'ICICI', 'SBI', 'AXIS', 'KOTAK', 'YESB', 'IDFC', 'IDBI', 'IndusInd'];

  const renderStringCondition = (
    field: keyof Conditions,
    label: string,
    suggestions: string[] = []
  ) => {
    const condition = conditions[field] as StringCondition;
    const validationMessages = getConditionValidationMessages(field);
    const hasErrors = validationMessages.some(msg => msg.level === 'error');

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>

        {validationMessages.map((msg, index) => (
          <Alert key={index} severity={msg.level} sx={{ mb: 1 }}>
            {msg.message}
          </Alert>
        ))}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel id={`operator-label-${field}`} >Operator</InputLabel>
              <Select
                labelId={`operator-label-${field}`}
                label="Operator"
                value={condition?.operator || 'equals'}
                onChange={(e) => handleStringConditionChange(field, 'operator', e.target.value)}
                error={hasErrors}
              >
                {stringOperators.map(op => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            {['in', 'not_in'].includes(condition?.operator || 'equals') ? (
              <Autocomplete
                multiple
                freeSolo
                options={suggestions}
                value={Array.isArray(condition?.value) ? condition.value : []}
                onChange={(_event, newValue) => {
                  handleStringConditionChange(field, 'value', newValue);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Values"
                    placeholder="Select or type values"
                    error={hasErrors}
                    helperText="Select from suggestions or type custom values"
                  />
                )}
              />
            ) : (
              <Autocomplete
                freeSolo
                options={suggestions}
                value={Array.isArray(condition?.value) ? condition.value[0] || '' : condition?.value || ''}
                onChange={(_event, newValue) => {
                  handleStringConditionChange(field, 'value', newValue || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Value"
                    placeholder="Select or type value"
                    error={hasErrors}
                    helperText="Select from suggestions or type custom value"
                  />
                )}
              />
            )}
          </Grid>
        </Grid>

        {suggestions.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Suggestions:
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {suggestions.map(suggestion => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                  onClick={() => {
                    const currentValue = condition?.value || '';
                    const isArrayOperator = ['in', 'not_in'].includes(condition?.operator || 'equals');

                    if (isArrayOperator) {
                      const currentArray = Array.isArray(currentValue) ? currentValue : [];
                      if (!currentArray.includes(suggestion)) {
                        handleStringConditionChange(field, 'value', [...currentArray, suggestion]);
                      }
                    } else {
                      handleStringConditionChange(field, 'value', suggestion);
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderNumericCondition = (field: keyof Conditions, label: string) => {
    const condition = conditions[field] as NumericCondition;
    const validationMessages = getConditionValidationMessages(field);
    const hasErrors = validationMessages.some(msg => msg.level === 'error');

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>

        {validationMessages.map((msg, index) => (
          <Alert key={index} severity={msg.level} sx={{ mb: 1 }}>
            {msg.message}
          </Alert>
        ))}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
             <InputLabel id={`operator-label-${field}`}>Operator</InputLabel>
              <Select
                labelId={`operator-label-${field}`}
                label="Operator"
                value={condition?.operator || 'equals'}
                onChange={(e) => handleNumericConditionChange(field, 'operator', e.target.value)}
                error={hasErrors}
              >
                {numericOperators.map(op => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            {condition?.operator === 'between' ? (
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Value"
                    value={Array.isArray(condition?.value) ? condition.value[0] || '' : ''}
                    onChange={(e) => {
                      const currentValue = Array.isArray(condition?.value) ? condition.value : [0, 0];
                      handleNumericConditionChange(field, 'value', [
                        parseFloat(e.target.value) || 0,
                        currentValue[1] || 0
                      ]);
                    }}
                    error={hasErrors}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Value"
                    value={Array.isArray(condition?.value) ? condition.value[1] || '' : ''}
                    onChange={(e) => {
                      const currentValue = Array.isArray(condition?.value) ? condition.value : [0, 0];
                      handleNumericConditionChange(field, 'value', [
                        currentValue[0] || 0,
                        parseFloat(e.target.value) || 0
                      ]);
                    }}
                    error={hasErrors}
                  />
                </Grid>
              </Grid>
            ) : (
              <TextField
                fullWidth
                type="number"
                label="Value"
                value={Array.isArray(condition?.value) ? condition.value[0] || '' : condition?.value || ''}
                onChange={(e) => handleNumericConditionChange(field, 'value', parseFloat(e.target.value) || 0)}
                error={hasErrors}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define the conditions that must be met for this rule to apply. Leave empty for a catch-all rule.
      </Typography>

      {/* Logical Operator */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="logical-operator-label">Logical Operator</InputLabel>
          <Select
            labelId="logical-operator-label"
            label="Logical Operator"
            value={conditions.logicalOperator || 'AND'}
            onChange={(e) => handleConditionChange('logicalOperator', e.target.value)}
         
          >
            <MenuItem value="AND">AND (all conditions must match)</MenuItem>
            <MenuItem value="OR">OR (any condition must match)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* String Conditions */}
      {conditions.transactionType && renderStringCondition('transactionType', 'Transaction Type', transactionTypes)}
      {conditions.paymentMode && renderStringCondition('paymentMode', 'Payment Mode', paymentModes)}
      {conditions.authorizationMode && renderStringCondition('authorizationMode', 'Authorization Mode', authModes)}
      {conditions.bank && renderStringCondition('bank', 'Bank', commonBanks)}

      {/* Numeric Conditions */}
      {conditions.amount && renderNumericCondition('amount', 'Amount')}

      {/* Add Condition Buttons */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Conditions:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {!conditions.transactionType && (
            <Chip
              label="+ Transaction Type"
              onClick={() => handleConditionChange('transactionType', { operator: 'equals', value: '' })}
              variant="outlined"
            />
          )}
          {!conditions.paymentMode && (
            <Chip
              label="+ Payment Mode"
              onClick={() => handleConditionChange('paymentMode', { operator: 'equals', value: '' })}
              variant="outlined"
            />
          )}
          {!conditions.authorizationMode && (
            <Chip
              label="+ Authorization Mode"
              onClick={() => handleConditionChange('authorizationMode', { operator: 'equals', value: '' })}
              variant="outlined"
            />
          )}
          {!conditions.bank && (
            <Chip
              label="+ Bank"
              onClick={() => handleConditionChange('bank', { operator: 'equals', value: '' })}
              variant="outlined"
            />
          )}
          {!conditions.amount && (
            <Chip
              label="+ Amount"
              onClick={() => handleConditionChange('amount', { operator: 'equals', value: 0 })}
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Remove Condition Buttons */}
      {Object.keys(conditions).filter(key => key !== 'logicalOperator').length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Remove Conditions:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.keys(conditions).filter(key => key !== 'logicalOperator').map(key => (
              <Chip
                key={key}
                label={`- ${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`}
                onClick={() => handleRemoveCondition(key as keyof Conditions)}
                variant="outlined"
                color="error"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ConditionsEditor;
